/**
 * @apv-erp/status — Offset Packaging Engineering Engine (20-Year Industry Standards)
 *
 * Pure, top-level exported domain calculation functions for:
 * 1. Tiered Make-Ready & Running Waste Matrix (Bù hao canh bài & bù hao chạy máy bậc thang)
 * 2. Area-Weighted Gang-Run Cost Allocation (Phân bổ giá thành ghép bài theo Diện tích x Số UP)
 * 3. CTP Plate Impression Durability & Baked Plate Guard (Giới hạn lượt in bản kẽm CTP & Nướng kẽm)
 * 4. Paper Grain Direction Compatibility Guard (Kiểm soát hướng sớ giấy chống nứt gân bế)
 * 5. Ink Curing & Acclimatization Delay (Thời gian chờ khô mực trước cán màng / bế)
 * 6. Printing Delivery Tolerance Evaluation (Dung sai giao hàng ngành in ±3%~5%)
 */

export interface OffsetEngineeringParams {
  setupWastePerColor: number;
  setupWasteMinBase: number;
  setupWastePostPressStep: number;
  runWasteTier1MaxSheets: number;
  runWasteTier1Pct: number;
  runWasteTier2MaxSheets: number;
  runWasteTier2Pct: number;
  runWasteTier3Pct: number;
  maxPlateImpressionsStandard: number;
  maxPlateImpressionsUv: number;
  inkCuringWaitHoursConventional: number;
  inkCuringWaitHoursUv: number;
  deliveryTolerancePct: number;
}

export const DEFAULT_OFFSET_ENGINEERING_PARAMS: OffsetEngineeringParams = {
  setupWastePerColor: 35,
  setupWasteMinBase: 50,
  setupWastePostPressStep: 50,
  runWasteTier1MaxSheets: 3000,
  runWasteTier1Pct: 3.0,
  runWasteTier2MaxSheets: 10000,
  runWasteTier2Pct: 1.8,
  runWasteTier3Pct: 1.0,
  maxPlateImpressionsStandard: 80000,
  maxPlateImpressionsUv: 40000,
  inkCuringWaitHoursConventional: 4,
  inkCuringWaitHoursUv: 0,
  deliveryTolerancePct: 5.0,
};

/**
 * Parse dynamic ConfigItem[] from DB category `offset_engineering_params`
 */
export function parseOffsetEngineeringParams(
  items?: Array<{ code?: string | null; value?: string | number | null; name?: string | null; data?: any }> | null
): OffsetEngineeringParams {
  const result: OffsetEngineeringParams = { ...DEFAULT_OFFSET_ENGINEERING_PARAMS };
  if (!items || !Array.isArray(items)) return result;

  for (const item of items) {
    const code = (item.code || '').toUpperCase().trim();
    const rawVal = item.value ?? item.data?.value ?? item.name;
    const numVal = Number(rawVal);
    if (Number.isNaN(numVal) || numVal < 0) continue;

    switch (code) {
      case 'SETUP_WASTE_PER_COLOR':
        result.setupWastePerColor = numVal;
        break;
      case 'SETUP_WASTE_MIN_BASE':
        result.setupWasteMinBase = numVal;
        break;
      case 'SETUP_WASTE_POSTPRESS_STEP':
        result.setupWastePostPressStep = numVal;
        break;
      case 'RUN_WASTE_TIER1_MAX_SHEETS':
        result.runWasteTier1MaxSheets = numVal;
        break;
      case 'RUN_WASTE_TIER1_PCT':
        result.runWasteTier1Pct = numVal;
        break;
      case 'RUN_WASTE_TIER2_MAX_SHEETS':
        result.runWasteTier2MaxSheets = numVal;
        break;
      case 'RUN_WASTE_TIER2_PCT':
        result.runWasteTier2Pct = numVal;
        break;
      case 'RUN_WASTE_TIER3_PCT':
        result.runWasteTier3Pct = numVal;
        break;
      case 'MAX_PLATE_IMPRESSIONS_STANDARD':
        result.maxPlateImpressionsStandard = numVal;
        break;
      case 'MAX_PLATE_IMPRESSIONS_UV':
        result.maxPlateImpressionsUv = numVal;
        break;
      case 'INK_CURING_WAIT_HOURS_CONVENTIONAL':
        result.inkCuringWaitHoursConventional = numVal;
        break;
      case 'INK_CURING_WAIT_HOURS_UV':
        result.inkCuringWaitHoursUv = numVal;
        break;
      case 'DELIVERY_TOLERANCE_PCT':
        result.deliveryTolerancePct = numVal;
        break;
    }
  }

  return result;
}

export interface TieredWasteResult {
  baseSheets: number;
  setupWasteSheets: number;
  runningWastePct: number;
  runningWasteSheets: number;
  totalWasteSheets: number;
  totalRunSheets: number;
  tierLabel: string;
  formulaSummary: string;
}

/**
 * 1. Ma trận Định mức Bù hao Bậc thang theo Số màu in & Sản lượng chạy máy
 * S_waste = S_setup(N_colors, N_postpress) + ceil(S_base * r_run(S_base))
 */
export function calculateTieredOffsetWaste(
  inputOrBaseSheets:
    | {
        baseSheets: number;
        numColors?: number;
        colorCount?: number;
        numPostPressSteps?: number;
        postPressStepCount?: number;
        isUvInk?: boolean;
        params?: Partial<OffsetEngineeringParams>;
      }
    | number,
  numColorsArg?: number,
  numPostPressStepsArg?: number,
  isUvInkArg?: boolean,
  paramsArg?: Partial<OffsetEngineeringParams>
): TieredWasteResult {
  const input =
    typeof inputOrBaseSheets === 'number'
      ? {
          baseSheets: inputOrBaseSheets,
          numColors: numColorsArg,
          numPostPressSteps: numPostPressStepsArg,
          isUvInk: isUvInkArg,
          params: paramsArg,
        }
      : inputOrBaseSheets || { baseSheets: 0 };

  const cfg: OffsetEngineeringParams = {
    ...DEFAULT_OFFSET_ENGINEERING_PARAMS,
    ...(input.params || {}),
  };
  const base = Math.max(0, Math.ceil(Number(input.baseSheets) || 0));
  const rawColors = input.numColors ?? input.colorCount;
  const colors = Math.max(1, Number(rawColors) || 4);
  const rawPostPress = input.numPostPressSteps ?? input.postPressStepCount;
  const parsedPostPress = Number(rawPostPress);
  const postPress = Math.max(0, Number.isFinite(parsedPostPress) ? parsedPostPress : 2);
  const uvMultiplier = input.isUvInk ? 1.2 : 1.0;

  const setupWasteSheets = Math.ceil(
    (cfg.setupWasteMinBase + colors * cfg.setupWastePerColor + postPress * cfg.setupWastePostPressStep) *
      uvMultiplier
  );

  let runningWastePct = cfg.runWasteTier1Pct;
  let tierLabel = `Bậc 1 (≤ ${cfg.runWasteTier1MaxSheets.toLocaleString('vi-VN')} tờ: ${cfg.runWasteTier1Pct}%)`;

  if (base > cfg.runWasteTier2MaxSheets) {
    runningWastePct = cfg.runWasteTier3Pct;
    tierLabel = `Bậc 3 (> ${cfg.runWasteTier2MaxSheets.toLocaleString('vi-VN')} tờ: ${cfg.runWasteTier3Pct}%)`;
  } else if (base > cfg.runWasteTier1MaxSheets) {
    runningWastePct = cfg.runWasteTier2Pct;
    tierLabel = `Bậc 2 (${cfg.runWasteTier1MaxSheets.toLocaleString('vi-VN')}–${cfg.runWasteTier2MaxSheets.toLocaleString('vi-VN')} tờ: ${cfg.runWasteTier2Pct}%)`;
  }

  const runningWasteSheets = Math.ceil((base * runningWastePct) / 100);
  const totalWasteSheets = setupWasteSheets + runningWasteSheets;
  const totalRunSheets = base + totalWasteSheets;

  return {
    baseSheets: base,
    setupWasteSheets,
    runningWastePct,
    runningWasteSheets,
    totalWasteSheets,
    totalRunSheets,
    tierLabel,
    formulaSummary: `Canh máy ${colors} màu (${setupWasteSheets} tờ) + Chạy máy ${runningWastePct}% (${runningWasteSheets} tờ) = ${totalWasteSheets} tờ bù hao`,
  };
}

export interface GangRunWeightItemInput {
  id?: string;
  itemId?: string;
  ups: number;
  flatLength?: number | null;
  flatWidth?: number | null;
  flatLengthMm?: number | null;
  flatWidthMm?: number | null;
  quantity?: number | null;
}

export interface GangRunWeightItemResult {
  id: string;
  itemId: string;
  ups: number;
  unitAreaMm2: number;
  totalOccupiedAreaMm2: number;
  costWeightRatio: number;
  costAllocationPct: number;
  areaWeightPercent: number;
  weightPercent: number;
  allocationMethod: 'AREA_X_UPS' | 'UPS_RATIO';
}

/**
 * 2. Phân bổ Giá thành Lệnh Ghép Gang-Run theo Tỷ trọng Diện tích × Số UP
 * w_i = (UPS_i * L_i * W_i) / sum_j(UPS_j * L_j * W_j)
 */
export function calculateGangRunAreaWeights(
  items: GangRunWeightItemInput[]
): GangRunWeightItemResult[] {
  if (!items || items.length === 0) return [];

  const prepared = items.map((item, idx) => {
    const ups = Math.max(1, Number(item.ups) || 1);
    const L = Math.max(0, Number(item.flatLength ?? item.flatLengthMm) || 0);
    const W = Math.max(0, Number(item.flatWidth ?? item.flatWidthMm) || 0);
    const unitAreaMm2 = L > 0 && W > 0 ? L * W : 0;
    const resolvedId = String(item.id || item.itemId || idx);
    return {
      id: resolvedId,
      itemId: resolvedId,
      ups,
      unitAreaMm2,
      totalOccupiedAreaMm2: unitAreaMm2 * ups,
    };
  });

  const allHaveArea = prepared.every((p) => p.totalOccupiedAreaMm2 > 0);
  const totalArea = prepared.reduce((acc, p) => acc + p.totalOccupiedAreaMm2, 0);
  const totalUps = prepared.reduce((acc, p) => acc + p.ups, 0) || 1;

  return prepared.map((p) => {
    const ratio =
      allHaveArea && totalArea > 0
        ? p.totalOccupiedAreaMm2 / totalArea
        : p.ups / totalUps;
    const pct = Number((ratio * 100).toFixed(2));
    return {
      id: p.id,
      itemId: p.itemId,
      ups: p.ups,
      unitAreaMm2: p.unitAreaMm2,
      totalOccupiedAreaMm2: p.totalOccupiedAreaMm2,
      costWeightRatio: Number(ratio.toFixed(4)),
      costAllocationPct: pct,
      areaWeightPercent: pct,
      weightPercent: pct,
      allocationMethod: allHaveArea && totalArea > 0 ? 'AREA_X_UPS' : 'UPS_RATIO',
    };
  });
}

export interface PlateDurabilityResult {
  runSheets: number;
  maxImpressionsPerSet: number;
  setsRequired: number;
  requiredSets: number;
  totalPlates: number;
  recommendBake: boolean;
  isOverSingleSetLimit: boolean;
  ExceedsSingleSet: boolean;
  utilizationPct: number;
  warningMessage: string | null;
  recommendation: string;
}

/**
 * 3. Quản lý Tuổi thọ Bản Kẽm CTP & Cờ Nướng Kẽm (Baked Plate Guard)
 */
export function evaluatePlateDurability(
  inputOrRunSheets:
    | {
        runSheets: number;
        numColors?: number;
        isUvInk?: boolean;
        isBaked?: boolean;
        params?: Partial<OffsetEngineeringParams>;
      }
    | number,
  numColorsArg?: number,
  isUvInkArg?: boolean,
  isBakedArg?: boolean,
  paramsArg?: Partial<OffsetEngineeringParams>
): PlateDurabilityResult {
  const input =
    typeof inputOrRunSheets === 'number'
      ? {
          runSheets: inputOrRunSheets,
          numColors: numColorsArg,
          isUvInk: isUvInkArg,
          isBaked: isBakedArg,
          params: paramsArg,
        }
      : inputOrRunSheets || { runSheets: 0 };

  const cfg: OffsetEngineeringParams = {
    ...DEFAULT_OFFSET_ENGINEERING_PARAMS,
    ...(input.params || {}),
  };
  const sheets = Math.max(0, Number(input.runSheets) || 0);
  const colors = Math.max(1, Number(input.numColors) || 4);
  const baseLimit = input.isUvInk
    ? cfg.maxPlateImpressionsUv
    : cfg.maxPlateImpressionsStandard;
  const maxImpressionsPerSet = input.isBaked ? Math.round(baseLimit * 2.5) : baseLimit;
  const setsRequired = sheets > 0 ? Math.max(1, Math.ceil(sheets / maxImpressionsPerSet)) : 1;
  const isOverSingleSetLimit = sheets > baseLimit;
  const recommendBake = isOverSingleSetLimit && !input.isBaked;
  const utilizationPct =
    maxImpressionsPerSet > 0 ? Math.min(999, Math.round((sheets / maxImpressionsPerSet) * 100)) : 0;

  let warningMessage: string | null = null;
  if (isOverSingleSetLimit) {
    warningMessage = `Vượt tuổi thọ 1 bộ kẽm: Sản lượng ${sheets.toLocaleString('vi-VN')} lượt in vượt ngưỡng 1 bộ kẽm ${input.isUvInk ? 'UV' : 'thường'} (${baseLimit.toLocaleString('vi-VN')} lượt). Đề xuất xuất ${setsRequired} bộ kẽm CTP hoặc Nướng kẽm (Baked Plate) chống mòn tram!`;
  }

  return {
    runSheets: sheets,
    maxImpressionsPerSet,
    setsRequired,
    requiredSets: setsRequired,
    totalPlates: setsRequired * colors,
    recommendBake,
    isOverSingleSetLimit,
    ExceedsSingleSet: isOverSingleSetLimit,
    utilizationPct,
    warningMessage,
    recommendation:
      warningMessage ||
      `Đủ tuổi thọ trong 1 bộ kẽm (${sheets.toLocaleString('vi-VN')}/${maxImpressionsPerSet.toLocaleString('vi-VN')} lượt in).`,
  };
}

export type GrainDirectionType = 'LONG' | 'SHORT' | 'ANY';

export interface GrainCompatibilityResult {
  isCompatible: boolean;
  valid: boolean;
  isValid: boolean;
  hasWarning: boolean;
  canOverride: boolean;
  sheetGrainAxis: 'LONG_EDGE' | 'SHORT_EDGE';
  warningMessage: string | null;
  errors: string[];
  warnings: string[];
  badgeText: string;
}

/**
 * 4. Khuyến Nghị Hướng Sớ Giấy (Paper Grain Direction Advisory)
 * Trong quy ước khổ giấy in W x L (VD: 650x860mm -> L=860 là chiều sớ giấy dọc Long Grain).
 * CHUẨN XƯỞNG THỰC TẾ: Không chặn cứng (non-blocking advisory), cho phép thợ tùy biến theo khổ giấy có sẵn.
 */
export function validateGrainDirectionCompatibility(
  inputOrItems:
    | {
        productGrain?: GrainDirectionType | string | null;
        strictGrainDirection?: boolean;
        isRotated90?: boolean;
        gsm?: number | null;
        sheetGrainDirection?: GrainDirectionType | string | null;
        items?: any[];
      }
    | Array<{
        id?: string;
        name?: string;
        grainDirection?: GrainDirectionType | string | null;
        productGrain?: GrainDirectionType | string | null;
        strictGrainDirection?: boolean;
        isRotated90?: boolean;
        gsm?: number | null;
      }>
): GrainCompatibilityResult {
  const items = Array.isArray(inputOrItems)
    ? inputOrItems
    : Array.isArray((inputOrItems as any)?.items)
    ? (inputOrItems as any).items
    : null;

  if (items) {
    const warnings: string[] = [];
    for (const item of items) {
      const single = validateGrainDirectionCompatibility({
        productGrain: item.grainDirection ?? item.productGrain ?? item.requiredGrain,
        strictGrainDirection: item.strictGrainDirection,
        isRotated90: item.isRotated90,
        gsm: item.gsm,
      });
      if (single.hasWarning && single.warningMessage) {
        warnings.push(`${item.name || item.productName ? `[${item.name || item.productName}] ` : ''}${single.warningMessage}`);
      }
    }
    return {
      isCompatible: true,
      valid: true,
      isValid: true,
      canOverride: true,
      hasWarning: warnings.length > 0,
      sheetGrainAxis: warnings.length > 0 ? 'SHORT_EDGE' : 'LONG_EDGE',
      warningMessage: warnings[0] || null,
      errors: [],
      warnings,
      badgeText:
        warnings.length > 0
          ? '⚠️ Sớ ngang (Khuyến nghị dùng dao rãnh mềm)'
          : 'Đúng sớ giấy dọc (Grain Long)',
    };
  }

  const input: any = inputOrItems || {};
  const grain = (input.productGrain || 'ANY').toUpperCase();
  const isHeavyStock = (Number(input.gsm) || 0) >= 250;
  const isStrict = Boolean(input.strictGrainDirection) || (grain !== 'ANY' && isHeavyStock);
  const rotated = Boolean(input.isRotated90);

  if (grain === 'ANY' || (!rotated && grain === 'LONG')) {
    return {
      isCompatible: true,
      valid: true,
      isValid: true,
      canOverride: true,
      hasWarning: false,
      sheetGrainAxis: 'LONG_EDGE',
      warningMessage: null,
      errors: [],
      warnings: [],
      badgeText: grain === 'ANY' ? 'Sớ giấy tự động (Auto Grain)' : 'Đúng sớ giấy dọc (Grain Long)',
    };
  }

  if (rotated && isStrict) {
    const msg =
      'Khuyến nghị Kỹ thuật: Con hàng đang xoay 90° ngược chiều sớ giấy chuẩn của hộp. Xưởng nên dùng dao bế rãnh mềm (creasing matrix) để chống nứt gân bế khi gấp.';
    return {
      isCompatible: true,
      valid: true,
      isValid: true,
      canOverride: true,
      hasWarning: true,
      sheetGrainAxis: 'SHORT_EDGE',
      warningMessage: msg,
      errors: [],
      warnings: [msg],
      badgeText: '⚠️ Sớ ngang (Khuyến nghị dùng dao rãnh mềm)',
    };
  }

  return {
    isCompatible: true,
    valid: true,
    isValid: true,
    canOverride: true,
    hasWarning: false,
    sheetGrainAxis: rotated ? 'SHORT_EDGE' : 'LONG_EDGE',
    warningMessage: null,
    errors: [],
    warnings: [],
    badgeText: rotated ? 'Xoay 90° (Sớ ngang)' : 'Sớ dọc chuẩn (Grain Long)',
  };
}

/**
 * 5. Thời gian Chờ Khô Mực trước khi Cán màng / Bồi sóng / Bế
 */
export function evaluateInkCuringDelay(
  inputOrPrevStep:
    | {
        prevStepName?: string | null;
        nextStepName?: string | null;
        previousStepName?: string | null;
        currentStepName?: string | null;
        previousStepCode?: string | null;
        currentStepCode?: string | null;
        isUvInk?: boolean;
        params?: Partial<OffsetEngineeringParams>;
      }
    | string
    | null
    | undefined,
  nextStepNameArg?: string | null,
  isUvInkArg?: boolean,
  paramsArg?: Partial<OffsetEngineeringParams>
): {
  requiresCuringWait: boolean;
  waitHours: number;
  curingDelayHours: number;
  reason: string | null;
} {
  const input =
    typeof inputOrPrevStep === 'string' || inputOrPrevStep === null || inputOrPrevStep === undefined
      ? {
          prevStepName: inputOrPrevStep,
          nextStepName: nextStepNameArg,
          isUvInk: isUvInkArg,
          params: paramsArg,
        }
      : inputOrPrevStep;

  const cfg: OffsetEngineeringParams = {
    ...DEFAULT_OFFSET_ENGINEERING_PARAMS,
    ...(input.params || {}),
  };
  const prev = (input.prevStepName || input.previousStepName || '').toLowerCase();
  const next = (input.nextStepName || input.currentStepName || '').toLowerCase();

  const isPrevPrinting =
    prev.includes('in offset') ||
    prev.includes('in flexo') ||
    prev.includes('print') ||
    prev.startsWith('in ') ||
    prev === 'in';
  const isNextSurfaceOrFinishing =
    next.includes('cán') ||
    next.includes('laminat') ||
    next.includes('bồi') ||
    next.includes('foil') ||
    next.includes('ép kim') ||
    next.includes('nhũ') ||
    next.includes('die') ||
    next.includes('bế');

  if (!isPrevPrinting || !isNextSurfaceOrFinishing) {
    return { requiresCuringWait: false, waitHours: 0, curingDelayHours: 0, reason: null };
  }

  const waitHours = input.isUvInk
    ? cfg.inkCuringWaitHoursUv
    : cfg.inkCuringWaitHoursConventional;

  if (waitHours <= 0) {
    return { requiresCuringWait: false, waitHours: 0, curingDelayHours: 0, reason: null };
  }

  return {
    requiresCuringWait: true,
    waitHours,
    curingDelayHours: waitHours,
    reason: `Chờ khô mực ${waitHours}h trước khi ${input.nextStepName || 'gia công sau in'} (chống bong màng / trầy mặt in)`,
  };
}

/**
 * 6. Đánh giá Dung sai Giao hàng Ngành In (±3% ~ 5%)
 */
export function evaluateDeliveryTolerance(
  inputOrOrderedQty:
    | {
        orderedQty: number;
        deliveredQty: number;
        tolerancePct?: number;
      }
    | number,
  deliveredQtyArg?: number,
  tolerancePctArg?: number
): {
  orderedQty: number;
  deliveredQty: number;
  fulfillmentPct: number;
  shortfallPct: number;
  deviationPercent: number;
  tolerancePct: number;
  minAllowedQty: number;
  maxAllowedQty: number;
  status: 'WITHIN_TOLERANCE' | 'UNDER_DELIVERED' | 'OVER_DELIVERED';
  isWithinTolerance: boolean;
  canCloseWithinTolerance: boolean;
} {
  const input =
    typeof inputOrOrderedQty === 'number'
      ? {
          orderedQty: inputOrOrderedQty,
          deliveredQty: Number(deliveredQtyArg || 0),
          tolerancePct: tolerancePctArg,
        }
      : inputOrOrderedQty || { orderedQty: 0, deliveredQty: 0 };

  const ordered = Math.max(0, Number(input.orderedQty) || 0);
  const delivered = Math.max(0, Number(input.deliveredQty) || 0);
  const tol =
    input.tolerancePct !== undefined && input.tolerancePct !== null
      ? Math.max(0, Number(input.tolerancePct))
      : DEFAULT_OFFSET_ENGINEERING_PARAMS.deliveryTolerancePct;

  const minAllowedQty = Math.ceil((ordered * (100 - tol)) / 100);
  const maxAllowedQty = Math.floor((ordered * (100 + tol)) / 100);

  if (ordered <= 0) {
    return {
      orderedQty: 0,
      deliveredQty: delivered,
      fulfillmentPct: 0,
      shortfallPct: 0,
      deviationPercent: 0,
      tolerancePct: tol,
      minAllowedQty: 0,
      maxAllowedQty: 0,
      status: 'UNDER_DELIVERED',
      isWithinTolerance: false,
      canCloseWithinTolerance: false,
    };
  }

  const fulfillmentPct = Number(((delivered / ordered) * 100).toFixed(2));
  const shortfallPct = Number(Math.max(0, 100 - fulfillmentPct).toFixed(2));
  const deviationPercent = Number((fulfillmentPct - 100).toFixed(2));
  const isWithinTolerance = fulfillmentPct >= 100 - tol && fulfillmentPct <= 100 + tol;
  const canCloseWithinTolerance = delivered > 0 && fulfillmentPct >= 100 - tol && delivered <= maxAllowedQty;
  const status: 'WITHIN_TOLERANCE' | 'UNDER_DELIVERED' | 'OVER_DELIVERED' = isWithinTolerance
    ? 'WITHIN_TOLERANCE'
    : fulfillmentPct < 100 - tol
      ? 'UNDER_DELIVERED'
      : 'OVER_DELIVERED';

  return {
    orderedQty: ordered,
    deliveredQty: delivered,
    fulfillmentPct,
    shortfallPct,
    deviationPercent,
    tolerancePct: tol,
    minAllowedQty,
    maxAllowedQty,
    status,
    isWithinTolerance,
    canCloseWithinTolerance,
  };
}

export const GRAIN_DIRECTION_OPTIONS = [
  { id: 'ANY', label: 'Tự do (Không khóa thớ)' },
  { id: 'LONG', label: '↕️ Thớ dọc (Grain Long - Song song cạnh dài)' },
  { id: 'SHORT', label: '↔️ Thớ ngang (Grain Short - Song song cạnh ngắn)' },
] as const;

export const FSC_CERTIFICATION_OPTIONS = [
  { id: 'NONE', label: 'Không yêu cầu FSC' },
  { id: 'FSC Mix Credit', label: '🌲 FSC Mix Credit (Chứng nhận CoC)' },
  { id: 'FSC Recycled 100%', label: '♻️ FSC Recycled 100% (Chứng nhận CoC)' },
  { id: 'FSC 100%', label: '🌲 FSC 100% Pure Forest' },
] as const;

